import { useTranslate } from '@tolgee/react';
import { useRequest } from 'ahooks';
import { Divider, message, Modal, ModalProps, Select, Switch, Tag } from 'antd';
import { ReactNode, useState } from 'react';
import { getReviewList } from './service';
import SafeImage from 'components/SafeImage';
import { isStoreAliasFirst, useStoreNameConfig } from 'hooks/business/store/useStoreName';
import { useGetRegionLabel } from 'hooks/business/useRegion';
import { platformLabelMap } from 'services/config/platform';
import toLowerCase from 'utils/toLowerCase';
import ReviewItemSelect from './ReviewItemSelect';
import ReviewTable from './ReviewTable';
import styled from '@emotion/styled';

export interface ReviewModalProps extends ModalProps {
  reviewIds: string[];
  count: number;
  isVoice?: boolean;
  uncategorized?: boolean;
  item?: ReviewAnalysis.Item;
  problem?: string;
  extra?: ReactNode;
}

function ReviewModal({
  reviewIds,
  count,
  isVoice = false,
  uncategorized = false,
  item,
  problem,
  extra,
  ...otherProps
}: ReviewModalProps) {
  const { t } = useTranslate();

  const [hasContentOnly, setHasContentOnly] = useState(uncategorized);
  const [selectedItemIds, setSelectedItemIds] = useState<string[]>([]);
  const [stars, setStars] = useState<number[]>([]);

  const { loading, data: { reviewList } = {} } = useRequest(
    () => {
      return getReviewList(reviewIds);
    },
    {
      onError: () => message.error(t('phrase.获取数据失败，请稍后再试')),
    },
  );

  return (
    <Modal
      width={1200}
      title={
        uncategorized ? t('未分类评价') : `${isVoice ? t('评价明细') : t('差评明细')}-${problem}`
      }
      footer={false}
      {...otherProps}
    >
      <Container>
        {item && <Item item={item} />}
        <header>
          <div>
            {reviewList &&
              (count > 1000
                ? t('共x条，仅展示1000条', { x: count })
                : t('phrase.共x条', { x: reviewList.length }))}
          </div>
          {reviewList?.length ? (
            <section>
              {extra}
              {uncategorized && (
                <span>
                  {t('仅看有内容的评价')}
                  <Switch checked={hasContentOnly} onChange={setHasContentOnly} />
                </span>
              )}
              {isVoice && (
                <Select
                  allowClear
                  mode="multiple"
                  placeholder={t('选择星级')}
                  style={{ width: 120 }}
                  value={stars}
                  onChange={setStars}
                  options={new Array(5).fill('').map((_, index) => ({
                    value: index + 1,
                    label: t('review.x星', { count: index + 1 }),
                  }))}
                  tagRender={({ value, label, onClose }) => {
                    if (stars.length === 1) {
                      return (
                        <Tag closable onClose={onClose}>
                          {label}
                        </Tag>
                      );
                    }
                    if (value === stars[0]) {
                      return (
                        <Tag closable onClose={() => setStars([])}>
                          {t('aide.已选x项', { x: stars.length })}
                        </Tag>
                      );
                    }
                    return <></>;
                  }}
                />
              )}
              {!item && (
                <ReviewItemSelect
                  data={reviewList || []}
                  value={selectedItemIds}
                  onChange={setSelectedItemIds}
                />
              )}
            </section>
          ) : null}
        </header>
        <section>
          <ReviewTable
            loading={loading}
            dataSource={reviewList}
            isVoice={isVoice}
            itemMode={Boolean(item)}
            hasContentOnly={hasContentOnly}
            selectedItemIds={selectedItemIds}
            stars={stars}
          />
        </section>
      </Container>
    </Modal>
  );
}

export function Item({
  item: { img, name, platform, region, storeName, storeAlias, spuId, itemSku },
}: {
  item: ReviewAnalysis.Item;
}) {
  const { t } = useTranslate();
  const getRegionLabel = useGetRegionLabel();

  const { config } = useStoreNameConfig();

  return (
    <ItemContainer>
      <SafeImage width={56} height={56} src={img} />
      <section>
        <p>{name}</p>
        <div>
          {platformLabelMap[toLowerCase(platform)]}-{getRegionLabel(region)}-
          {isStoreAliasFirst(config, platform, region) ? storeAlias || storeName : storeName}
          <Divider type="vertical" />
          ID：
          {spuId}
          <Divider type="vertical" />
          {t('主商品货号')}：{itemSku}
        </div>
      </section>
    </ItemContainer>
  );
}

const Container = styled.div`
  display: flex;
  flex-direction: column;
  height: calc(100vh - 104px);
  > header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 12px 0;
    &:first-child {
      padding-top: 0;
    }
    .ant-select {
      width: 280px;
      margin-left: 14px;
    }
    .ant-switch {
      margin-left: 4px;
    }
  }
  > * {
    flex: 0 0 auto;
  }
  > section {
    flex: 1 0 0;
  }
`;

const ItemContainer = styled.div`
  display: flex;
  padding: 10px 12px;
  border: solid 1px #f0f2f5;
  > div {
    flex: 0 0 auto;
    margin-right: 10px;
  }
  > section {
    flex: 1 0 0;
    display: flex;
    flex-direction: column;
    justify-content: space-around;
    overflow: hidden;
    > p {
      margin-bottom: 0;
      overflow: hidden;
      text-overflow: ellipsis;
      white-space: nowrap;
    }
    > div {
      font-size: 12px;
      color: #84868c;
    }
  }
`;

export default ReviewModal;
